'use client';
import { factory, useMergedRef, Box } from '@kohler-dsm-library/kohler-core';
import clsx from 'clsx';
import React, { useState, useRef, useImperativeHandle, useEffect } from 'react';
import { useStickyNavigation } from './MainMenu.hook.mjs';
import classes from './MainMenu.module.css.mjs';
import './subcomponents/LanguageMenu/LanguageMenu.mjs';
import './subcomponents/LoginMenu/LoginMenu.mjs';
import { MainNavigationBar } from './subcomponents/MainNavigationBar/MainNavigationBar.mjs';
import { MessageBanner } from './subcomponents/MessageBanner/MessageBanner.mjs';
import './subcomponents/OverlayMenu/OverlayMenu.mjs';
import './subcomponents/StoreMenu/StoreMenu.mjs';
import { UtilityBar } from './subcomponents/UtilityBar/UtilityBar.mjs';

const MainMenu = factory((props, ref) => {
  const {
    className,
    infoBanner,
    globalBanner,
    mainNavigation,
    utilityBar,
    promoBanner,
    storeMenu,
    loginMenu,
    languageMenu,
    mod,
    style,
    ...rest
  } = props;
  const classNames = clsx(classes.mainMenu, className, "kohler-main-menu-root");
  const [menuType, setMenuType] = useState(null);
  const [mainMenuHeight, setMainMenuHeight] = useState(0);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const mainMenuWrapperRef = useRef(null);
  const mainNavigationRef = useRef(null);
  const composeMainMenuWrapperRef = useMergedRef(mainMenuWrapperRef, ref);
  const { showStickyNav, isSticky } = useStickyNavigation(mainMenuHeight, mainNavigation?.isAlwaysSticky);
  const [firstStickyApplied, setFirstStickyApplied] = useState(false);
  useImperativeHandle(ref, () => {
    const domNode = mainMenuWrapperRef.current;
    if (!domNode) return null;
    return Object.assign(domNode, {
      setMenuType
    });
  });
  useEffect(() => {
    if (isSticky && !firstStickyApplied) {
      setFirstStickyApplied(true);
    } else if (!isSticky && firstStickyApplied) {
      setFirstStickyApplied(false);
    }
  }, [isSticky, firstStickyApplied]);
  const disableTransition = isSticky && !firstStickyApplied;
  const typedUtilityBar = {
    ...utilityBar,
    storeMenu,
    loginMenu,
    languageMenu,
    menuType,
    setMenuType,
    setIsOpenMobileMenu: setOpenMobileMenu
  };
  if (typedUtilityBar) {
    if (typedUtilityBar.languagePicker && languageMenu) {
      const originalOnClick = utilityBar?.languagePicker?.languagePickerCTA?.onClick;
      typedUtilityBar.languagePicker.languagePickerCTA.onClick = (e) => {
        if (originalOnClick) {
          originalOnClick(e);
        }
        if (menuType === "language") {
          setMenuType(null);
        } else {
          setMenuType("language");
        }
      };
    }
    if (typedUtilityBar.locationPickerCTA && storeMenu) {
      const originalOnClick = utilityBar?.locationPickerCTA?.onClick;
      typedUtilityBar.locationPickerCTA.onClick = (e) => {
        if (originalOnClick) {
          originalOnClick(e);
        }
        if (menuType === "store") {
          setMenuType(null);
        } else {
          setMenuType("store");
        }
      };
    }
    if (typedUtilityBar && loginMenu) {
      const originalOnClick = utilityBar?.userCTA?.onClick;
      typedUtilityBar.userCTA = utilityBar?.userCTA || {};
      typedUtilityBar.userCTA.onClick = (e) => {
        if (originalOnClick) {
          originalOnClick(e);
        }
        setMenuType(menuType === "user" ? null : "user");
      };
    }
  }
  useEffect(() => {
    if (mainMenuWrapperRef.current) {
      setMainMenuHeight(mainMenuWrapperRef.current.clientHeight);
    }
  }, [setMainMenuHeight]);
  useEffect(() => {
    if (mainNavigationRef.current) {
      const height = mainNavigationRef.current.clientHeight;
      document.body.style.setProperty("--main-menu-height", `${showStickyNav ? height : 0}px`);
    }
  }, [showStickyNav]);
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      component: "header",
      ref: composeMainMenuWrapperRef,
      ...rest,
      className: classNames,
      mod,
      style: { "--main-menu-height": `${mainMenuHeight}px`, ...style }
    },
    infoBanner && /* @__PURE__ */ React.createElement(MessageBanner, { type: "info", ...infoBanner }),
    globalBanner && globalBanner.content && globalBanner.content.length > 0 && /* @__PURE__ */ React.createElement(MessageBanner, { type: "global", ...globalBanner }),
    typedUtilityBar && /* @__PURE__ */ React.createElement(
      UtilityBar,
      {
        ...typedUtilityBar,
        menuType,
        setMenuType,
        storeMenu,
        languageMenu,
        loginMenu,
        setIsOpenMobileMenu: setOpenMobileMenu
      }
    ),
    promoBanner && promoBanner.content && promoBanner.content.length > 0 && /* @__PURE__ */ React.createElement(MessageBanner, { type: "promo", ...promoBanner }),
    mainNavigation && /* @__PURE__ */ React.createElement(
      Box,
      {
        className: clsx(classes.mainNavigationBarWrapper, {
          [classes.fixedNav]: isSticky,
          [classes.showSticky]: showStickyNav,
          [classes.showBackgroundRelative]: mainNavigation.showBackground || mainNavigation.isStatic
        }),
        style: disableTransition ? { transitionDuration: "0s" } : void 0
      },
      /* @__PURE__ */ React.createElement(
        MainNavigationBar,
        {
          ...mainNavigation,
          utilityLinks: typedUtilityBar,
          setIsOpenMobileMenu: setOpenMobileMenu,
          openMobileMenu,
          ref: mainNavigationRef,
          showBackground: isSticky || mainNavigation.showBackground
        }
      )
    )
  );
});

export { MainMenu };
